import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'

import {customerContactRefRawSchema, customerContactRefSchema} from '../customers/schemas'
import {parseApiDate, toApiDateString} from '../jobs/job-schedule-format'
import type {ChargeType, QuoteRentalPeriod, QuoteUnit} from './quote-builder-logic'
import {QUOTE_UNITS} from './quote-builder-logic'

const quoteStatusSchema = z.enum(['draft', 'sent', 'accepted', 'rejected', 'voided'])

export const quotePdfExportInputSchema = z.object({quoteId: z.string().trim().min(1)})
export type QuotePdfExportInput = z.infer<typeof quotePdfExportInputSchema>

const quoteLineItemKindWireSchema = z.enum(['item', 'percentage'])
const chargeTypeSchema = z.enum(['disposal', 'washout', 'hsei', 'transportation'])

const quoteUnitSchema = z.enum(QUOTE_UNITS)

const quoteLineItemRawSchema = z.object({
    id: z.string(),
    position: z.number(),
    kind: quoteLineItemKindWireSchema,
    description: z.string(),
    quantity: z.string().nullable(),
    duration: z.string().nullable(),
    unit: z.string().nullable(),
    rate: z.string(),
    amount: z.string(),
    linked_charge_type: chargeTypeSchema.nullable().optional(),
    for_rental_period: z.boolean().optional(),
    period_count: z.number().nullable().optional(),
})

export const quoteLineItemSchema = quoteLineItemRawSchema.transform((raw) => ({
    id: raw.id,
    position: raw.position,
    kind: raw.kind === 'percentage' ? ('percent' as const) : ('item' as const),
    description: raw.description,
    quantity: raw.quantity == null ? null : Number(raw.quantity),
    duration: raw.duration == null ? null : Number(raw.duration),
    unit: raw.unit as QuoteUnit | null,
    rate: Number(raw.rate),
    amount: Number(raw.amount),
    chargeType: raw.linked_charge_type ?? undefined,
    forRentalPeriod: raw.for_rental_period ?? false,
    periodCount: raw.period_count ?? null,
}))

export type QuoteLineItemRaw = z.infer<typeof quoteLineItemRawSchema>
export type QuoteLineItem = z.infer<typeof quoteLineItemSchema>

const quoteListItemRawSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    project_id: z.string().nullable(),
    name: z.string().nullable(),
    status: quoteStatusSchema,
    created_at: z.string(),
    created_by_id: z.string(),
    created_by_name: z.string(),
    line_item_count: z.number(),
    total: z.string(),
})

export const quoteListItemSchema = quoteListItemRawSchema.transform((raw) => ({
    id: raw.id,
    customerId: raw.customer_id,
    projectId: raw.project_id,
    name: raw.name,
    status: raw.status,
    createdAt: raw.created_at,
    createdById: raw.created_by_id,
    createdByName: raw.created_by_name,
    lineItemCount: raw.line_item_count,
    total: Number(raw.total),
}))

export type QuoteListItemRaw = z.infer<typeof quoteListItemRawSchema>
export type QuoteListItem = z.infer<typeof quoteListItemSchema>
export type QuoteStatus = z.infer<typeof quoteStatusSchema>

const quoteRequirementRawSchema = z.object({
    id: z.string(),
    quote_line_item_id: z.string(),
    kind: z.enum(['worker', 'equipment']),
    worker_type_id: z.string().nullable(),
    rental_asset_type_id: z.string().nullable(),
    type_name: z.string(),
    qty: z.number(),
    position: z.number(),
})

export const quoteRequirementSchema = quoteRequirementRawSchema.transform((raw) => ({
    id: raw.id,
    quoteLineItemId: raw.quote_line_item_id,
    kind: raw.kind,
    workerTypeId: raw.worker_type_id,
    rentalAssetTypeId: raw.rental_asset_type_id,
    typeName: raw.type_name,
    qty: raw.qty,
    position: raw.position,
}))

export type QuoteRequirementRaw = z.infer<typeof quoteRequirementRawSchema>
export type QuoteRequirement = z.infer<typeof quoteRequirementSchema>

const quoteRequirementWriteInputSchema = z.discriminatedUnion('kind', [
    z.object({
        quoteLineItemId: z.string(),
        kind: z.literal('worker'),
        qty: z.number().int().positive(),
        workerTypeId: z.string(),
    }),
    z.object({
        quoteLineItemId: z.string(),
        kind: z.literal('equipment'),
        qty: z.number().int().positive(),
        rentalAssetTypeId: z.string(),
    }),
])

export type QuoteRequirementWriteInput = z.infer<typeof quoteRequirementWriteInputSchema>

export const quoteRequirementWriteRequestSchema = quoteRequirementWriteInputSchema.transform(
    (requirement) => {
        if (requirement.kind === 'worker') {
            return {
                quote_line_item_id: requirement.quoteLineItemId,
                kind: requirement.kind,
                qty: requirement.qty,
                worker_type_id: requirement.workerTypeId,
            }
        }

        return {
            quote_line_item_id: requirement.quoteLineItemId,
            kind: requirement.kind,
            qty: requirement.qty,
            rental_asset_type_id: requirement.rentalAssetTypeId,
        }
    },
)

export type QuoteRequirementWriteRequestRaw = z.infer<typeof quoteRequirementWriteRequestSchema>

const quoteRentalPeriodRawSchema = z.object({
    months: z.number().nullable(),
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    days: z.number(),
})

function rentalPeriodFromRaw(
    raw: z.infer<typeof quoteRentalPeriodRawSchema> | null,
): QuoteRentalPeriod | null {
    if (raw == null) {
        return null
    }
    if (raw.months != null) {
        return {kind: 'months', months: raw.months}
    }
    if (raw.start_date != null && raw.end_date != null) {
        return {
            kind: 'dates',
            startDate: parseApiDate(raw.start_date),
            endDate: parseApiDate(raw.end_date),
        }
    }
    return null
}

const quoteRawSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    project_id: z.string().nullable(),
    name: z.string().nullable(),
    po_number: z.string().nullable(),
    terms: z.string().nullable(),
    due_date: z.string().nullable(),
    customer_contact: customerContactRefRawSchema.nullable().default(null),
    status: quoteStatusSchema,
    created_at: z.string(),
    created_by_id: z.string(),
    created_by_name: z.string(),
    archived_at: z.string().nullable(),
    archived_by_id: z.string().nullable(),
    sent_at: z.string().nullable(),
    sent_by_name: z.string().nullable(),
    rental_period: quoteRentalPeriodRawSchema.nullable(),
    line_items: z.array(quoteLineItemRawSchema),
    requirements: z.array(quoteRequirementRawSchema).default([]),
    subtotal: z.string(),
    total: z.string(),
})

export const quoteSchema = quoteRawSchema.transform((raw) => ({
    id: raw.id,
    customerId: raw.customer_id,
    projectId: raw.project_id,
    name: raw.name,
    poNumber: raw.po_number,
    terms: raw.terms,
    dueDate: raw.due_date == null ? null : parseApiDate(raw.due_date),
    customerContact:
        raw.customer_contact == null ? null : customerContactRefSchema.parse(raw.customer_contact),
    status: raw.status,
    createdAt: raw.created_at,
    createdById: raw.created_by_id,
    createdByName: raw.created_by_name,
    archivedAt: raw.archived_at,
    archivedById: raw.archived_by_id,
    sentAt: raw.sent_at == null ? null : tzDateFromIso.parse(raw.sent_at),
    sentByName: raw.sent_by_name,
    rentalPeriod: rentalPeriodFromRaw(raw.rental_period),
    lineItems: raw.line_items.map((lineItem) => quoteLineItemSchema.parse(lineItem)),
    requirements: raw.requirements.map((requirement) => quoteRequirementSchema.parse(requirement)),
    subtotal: Number(raw.subtotal),
    total: Number(raw.total),
}))

export type QuoteRaw = z.infer<typeof quoteRawSchema>
export type Quote = z.infer<typeof quoteSchema>

const quoteLineItemWriteInputSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('item'),
        description: z.string().trim().min(1),
        rate: z.number(),
        quantity: z.number(),
        duration: z.number().nullable(),
        unit: quoteUnitSchema,
        chargeType: chargeTypeSchema.optional(),
        forRentalPeriod: z.boolean().optional(),
    }),
    z.object({
        kind: z.literal('percent'),
        description: z.string().trim().min(1),
        rate: z.number(),
        chargeType: chargeTypeSchema.optional(),
    }),
])

export type QuoteLineItemWriteInput = z.infer<typeof quoteLineItemWriteInputSchema>

function linkedChargeTypeRequestField(chargeType: ChargeType | undefined) {
    return chargeType == null ? {} : {linked_charge_type: chargeType}
}

const quoteLineItemWriteRequestSchema = quoteLineItemWriteInputSchema.transform((line) => {
    if (line.kind === 'item') {
        return {
            kind: 'item' as const,
            description: line.description,
            rate: line.rate,
            quantity: line.quantity,
            duration: line.duration,
            unit: line.unit,
            ...(line.forRentalPeriod ? {for_rental_period: true} : {}),
            ...linkedChargeTypeRequestField(line.chargeType),
        }
    }

    return {
        kind: 'percentage' as const,
        description: line.description,
        rate: line.rate,
        ...linkedChargeTypeRequestField(line.chargeType),
    }
})

const rentalPeriodInputSchema = z.discriminatedUnion('kind', [
    z.object({kind: z.literal('months'), months: z.number().int().positive()}),
    z.object({kind: z.literal('dates'), startDate: z.date(), endDate: z.date()}),
])

function rentalPeriodRequest(period: z.infer<typeof rentalPeriodInputSchema> | null) {
    if (period == null) {
        return null
    }

    return period.kind === 'months'
        ? {months: period.months}
        : {
              start_date: toApiDateString(period.startDate),
              end_date: toApiDateString(period.endDate),
          }
}

export const createQuoteInputSchema = z.object({
    customerId: z.string(),
    customerContactId: z.string().nullable().optional(),
    name: z.string().trim().optional(),
    poNumber: z.string().trim().optional(),
    terms: z.string().trim().optional(),
    dueDate: z.date().nullable().optional(),
    status: quoteStatusSchema.optional(),
    lineItems: z.array(quoteLineItemWriteInputSchema).optional(),
    rentalPeriod: rentalPeriodInputSchema.nullable().optional(),
})

export type CreateQuoteInput = z.input<typeof createQuoteInputSchema>

export const createQuoteRequestSchema = createQuoteInputSchema.transform(
    ({
        customerId,
        customerContactId,
        name,
        poNumber,
        terms,
        dueDate,
        status,
        lineItems,
        rentalPeriod,
    }) => ({
        customer_id: customerId,
        customer_contact_id: customerContactId ?? null,
        name: name && name.length > 0 ? name : null,
        po_number: poNumber && poNumber.length > 0 ? poNumber : null,
        terms: terms && terms.length > 0 ? terms : null,
        due_date: dueDate == null ? null : toApiDateString(dueDate),
        status: status ?? 'draft',
        line_items: (lineItems ?? []).map((lineItem) =>
            quoteLineItemWriteRequestSchema.parse(lineItem),
        ),
        ...(rentalPeriod != null ? {rental_period: rentalPeriodRequest(rentalPeriod)} : {}),
    }),
)

export type CreateQuoteRequestRaw = z.infer<typeof createQuoteRequestSchema>

export const updateQuoteInputSchema = z.object({
    customerContactId: z.string().nullable().optional(),
    name: z.string().trim().optional(),
    poNumber: z.string().trim().optional(),
    terms: z.string().trim().optional(),
    dueDate: z.date().nullable().optional(),
    status: quoteStatusSchema.optional(),
    lineItems: z.array(quoteLineItemWriteInputSchema).optional(),
    rentalPeriod: rentalPeriodInputSchema.nullable().optional(),
})

export type UpdateQuoteInput = z.input<typeof updateQuoteInputSchema>

export const updateQuoteRequestSchema = updateQuoteInputSchema.transform(
    ({customerContactId, name, poNumber, terms, dueDate, status, lineItems, rentalPeriod}) => ({
        ...(customerContactId !== undefined ? {customer_contact_id: customerContactId} : {}),
        ...(name !== undefined ? {name: name.length > 0 ? name : null} : {}),
        ...(poNumber !== undefined ? {po_number: poNumber.length > 0 ? poNumber : null} : {}),
        ...(terms !== undefined ? {terms: terms.length > 0 ? terms : null} : {}),
        ...(dueDate !== undefined
            ? {due_date: dueDate == null ? null : toApiDateString(dueDate)}
            : {}),
        ...(status !== undefined ? {status} : {}),
        ...(lineItems !== undefined
            ? {
                  line_items: lineItems.map((lineItem) =>
                      quoteLineItemWriteRequestSchema.parse(lineItem),
                  ),
              }
            : {}),
        ...(rentalPeriod !== undefined ? {rental_period: rentalPeriodRequest(rentalPeriod)} : {}),
    }),
)

export type UpdateQuoteRequestRaw = z.infer<typeof updateQuoteRequestSchema>
